import * as angular from 'angular';
import * as _ from 'lodash';

import {
    ComponentDefinition,
    ConnectionPoint,
    ConnectionPointType,
    ContextualField,
    Dictionary,
    EventTrigger,
    ExportMode,
    Group,
    HistorianStorageConfiguration,
    IdentifierQuery,
    Miniature,
    NamedQueriesDto,
    PropertyType
} from '../../../../models';
import { IHistorianStorageConfigurationService, ILoggerService, IExportConfigurationService, ExportContextualConfiguration } from '../../../../services';
import { historianStorageConfigurationSaved, historianStorageConfigurationSaving } from '../../../../common/events';
import { exportConnectionPointTypes, severityLevels } from '../../../../common/constants';
import { Helper } from '../../../../common';
import * as Fields from '../../../../common/helpers/contextual';

class ModernHistorianStorageContextualController implements angular.IComponentController {
    allConnectionPointTypes: { [key: number]: string };
    exportConnectionPointTypes = exportConnectionPointTypes;
    formController: angular.IFormController;
    groups: Group[];
    isApproval: boolean = false;
    properties: ContextualField[];
    widgetsMiniature: Miniature[];

    readonly queries: IdentifierQuery[];
    readonly selectedItem: HistorianStorageConfiguration;

    private _configurations: HistorianStorageConfiguration[];
    private _connectionPoints: Dictionary<ConnectionPoint>;
    private _fieldsIndex: Dictionary<ContextualField>;
    private _filters: ContextualField[];
    private _namedQueries: Dictionary<NamedQueriesDto>;

    static $inject = ['$rootScope', '$timeout', 'exportConfigurationService', 'historianStorageService', 'loggerService'];
    constructor(
        private _$rootscope: angular.IRootScopeService,
        private _$timeout: angular.ITimeoutService,
        private _exportConfigurationService: IExportConfigurationService,
        private _configurationService: IHistorianStorageConfigurationService,
        private _loggerService: ILoggerService
    ) { }

    $onInit() {
        this._exportConfigurationService
            .getConfiguration()
            .then((data) => {
                this._configurations = data.configurations;
                this._connectionPoints = data.connectionPoints;
                this._namedQueries = data.namedQueries;
                this.allConnectionPointTypes = data.allConnectionPointTypes;
                this.groups = data.groups;
                this.widgetsMiniature = data.widgetMiniatures;
                this.initProperties(data);
                if (this.selectedItem) {
                    this.updateVisibility(this.selectedItem.trigger);
                }
            })
            .catch((reason) => {
                this._loggerService.logError(reason);
            });
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['selectedItem']?.currentValue && this.properties) {
            this.isApproval = this.selectedItem.trigger === EventTrigger.Approval || this.selectedItem.trigger === EventTrigger.Rejection;
            this.updateVisibility(this.selectedItem.trigger);
        }
    }

    initProperties(data: ExportContextualConfiguration): void {
        const groupField = Object.assign({}, Fields.GroupGuid);
        groupField.extraData = { groups: this.groups };

        const approvalLevel = Object.assign({}, Fields.ApprovalLevel);
        approvalLevel.extraData = { options: data.levels };

        const connectionPointField = Object.assign({}, Fields.ConnectionPointGuid);
        connectionPointField.propertyType = PropertyType.Select;
        connectionPointField.extraData = {
            options: this.widgetsMiniature.map((m) => m.data).map((d) => ({ key: d.guid, value: d.name }))
        };

        const nameField = Object.assign({}, Fields.Name);
        nameField.placeholder = 'Configuration name';
        nameField.isValid = (value) => {
            const hasDuplicate = _.some(this._configurations, (c) => c.guid !== this.selectedItem.guid && c.name === value);
            return Helper.getDictionnary<boolean>([{ key: 'duplicate', value: !hasDuplicate }]);
        };
        const exportModeProperty: ContextualField = {
            label: 'Export mode',
            modelName: 'exportMode',
            propertyName: 'exportMode',
            propertyType: PropertyType.Select,
            icon: 'fa-neuter',
            messages: [
                {
                    key: 'invalid',
                    value: 'This connection point does not support export by logbook entry'
                }
            ],
            extraData: {
                options: [
                    { key: ExportMode.ByIdentifier, value: 'By identifier' },
                    { key: ExportMode.ByLogbookEntry, value: 'By logbook entry' }
                ]
            },
            isValid: (value) => {
                const cp = this._connectionPoints[this.selectedItem.connectionPointGuid];
                const isExportModeValid = cp?.type === ConnectionPointType.SQL || value === ExportMode.ByIdentifier;
                return { invalid: isExportModeValid };
            }
        };

        const triggerField: ContextualField = {
            modelName: 'trigger',
            propertyName: 'triggerProperty',
            propertyType: PropertyType.Select,
            label: 'Trigger',
            placeholder: 'Trigger',
            required: true,
            iconText: '🗲',
            messages: [{ key: 'invalid', value: 'Manual trigger is not supported for this connection point type and export mode combination' }],
            extraData: {
                options: [
                    { key: EventTrigger.Scheduled, value: 'Scheduled' },
                    { key: EventTrigger.Approval, value: 'Approval' },
                    { key: EventTrigger.Rejection, value: 'Rejection' },
                    { key: EventTrigger.Manual, value: 'Manual' }
                ]
            },
            onChange: (viewValue) => {
                if (!this.isShowCustomTimestamp(this.selectedItem.connectionPointGuid, viewValue)) {
                    this.selectedItem.isCustomTimestamp = false;
                }
                const isTriggerValid = this.isTriggerValid({
                    connectionPointGuid: this.selectedItem.connectionPointGuid,
                    exportMode: this.selectedItem.exportMode,
                    trigger: viewValue
                });
                this.formController[triggerField.propertyName].$setValidity('invalid', isTriggerValid);

                return this.updateVisibility(viewValue);
            }
        };
        const isCustomTimestampField = {
            hide: !this.isShowCustomTimestamp(this.selectedItem.connectionPointGuid, this.selectedItem.trigger),
            icon: 'fa-calendar',
            label: 'Allow user to manage approval/rejection and historian storage timestamps separately',
            modelName: 'isCustomTimestamp',
            placeholder: 'Custom Historian Storage Timestamp',
            propertyName: 'isCustomTimestampProperty',
            propertyType: PropertyType.Boolean,
            required: false,
            type: 'boolean'
        };
        connectionPointField.onChange = (value: string) => {
            if (!this.isShowCustomTimestamp(value, this.selectedItem.trigger)) {
                this.selectedItem.isCustomTimestamp = false;
            }
            const isExportModeValid = this.isExportModeValid(value, this.selectedItem.exportMode);
            this.formController[exportModeProperty.propertyName].$setValidity('invalid', isExportModeValid);
            const isTriggerValid = this.isTriggerValid({
                connectionPointGuid: value,
                exportMode: this.selectedItem.exportMode,
                trigger: this.selectedItem.trigger
            });
            this.formController[triggerField.propertyName].$setValidity('invalid', isTriggerValid);
            this._$timeout(() => this.updateVisibility(this.selectedItem.trigger));
        };

        const readModeProperty = angular.copy(Fields.ReadMode);

        const properties: ContextualField[] = [
            nameField,
            Object.assign({}, Fields.ShortComment),
            Object.assign({}, Fields.LongComment),
            connectionPointField,
            exportModeProperty,
            readModeProperty,
            {
                modelName: 'exportQueryName',
                propertyType: PropertyType.Select,
                propertyName: 'exportQueryNameProperty',
                label: 'Export query',
                required: true,
                icon: 'fa-database',
                messages: [{ key: 'required', value: 'An export query is required' }]
            },
            {
                modelName: 'hasTransactionsTracking',
                propertyType: PropertyType.Boolean,
                propertyName: 'hasTransactionsTrackingProperty',
                label: 'Trace transactions',
                placeholder: 'Trace transactions',
                type: 'boolean',
                required: false,
                icon: 'fa-handshake-o'
            },
            {
                modelName: 'renamePattern',
                propertyName: 'renamePatternProperty',
                propertyType: PropertyType.Text,
                label: 'Naming pattern',
                placeholder: 'Naming pattern',
                type: 'text',
                required: true,
                iconText: 'XYZ',
                helpReference: '_Exporting_data/Storing_into_historian/#naming-pattern'
            },
            {
                modelName: 'toUppercase',
                propertyName: 'toUppercasePatternProperty',
                propertyType: PropertyType.Boolean,
                label: 'To UPPERCASE',
                placeholder: 'To UPPERCASE',
                required: false,
                iconText: 'U'
            },
            {
                modelName: 'groupByPattern',
                propertyName: 'groupByPatternProperty',
                propertyType: PropertyType.Text,
                label: 'Group by',
                placeholder: 'Group by',
                type: 'text',
                required: false,
                icon: 'fa-puzzle-piece',
                helpReference: '_Exporting_data/Storing_into_historian/#group-by'
            },
            {
                modelName: 'queryCollection',
                propertyType: PropertyType.Queries,
                propertyName: 'queryCollectionProperty',
                label: 'Queries',
                placeholder: 'Queries',
                required: true,
                icon: 'fa-filter',
                messages: [
                    { key: 'required', value: 'A query is required' },
                    { key: 'multiAggregation', value: 'Linked queries need exactly one aggregation mode to export data by logbook entry' }
                ],
                isValid: (value: { guid: string; name: string }[]) => {
                    let hasOneAggregationMode = false;
                    if (this.selectedItem.exportMode === ExportMode.ByIdentifier) {
                        hasOneAggregationMode = true;
                    } else if (this.queries) {
                        const am = [];
                        value.forEach((v) => {
                            const query = _.find(this.queries, (q) => q.guid === v.guid);
                            if (!!query) {
                                query.aggregationModes.filter((a) => am.indexOf(a) < 0).forEach((a) => am.push(a));
                            }
                        });
                        hasOneAggregationMode = am.length === 1;
                    }
                    return {
                        required: !!value?.length,
                        multiAggregation: hasOneAggregationMode
                    };
                },
                onChange: (value: string[]) => { }
            },
            Object.assign({}, Fields.IsBoundToGroup),
            triggerField,
            isCustomTimestampField,
            Object.assign({}, Fields.GroupGuids),
            Object.assign({}, Fields.GroupTypes),
            approvalLevel,
            Object.assign({}, Fields.Min),
            Object.assign({}, Fields.Max, { addSeparatorAfter: true })
        ];

        this._fieldsIndex = {};
        properties.forEach((f) => (this._fieldsIndex[f.modelName] = f));

        const groupGuidsProperty = Object.assign({}, Fields.GroupGuids, {
            propertyName: 'lbeGroupGuidsProperty',
            required: false
        });
        const groupTypesProperty = Object.assign({}, Fields.GroupTypes, { propertyName: 'lbeGroupTypesProperty' });
        let filters = [
            Object.assign({}, Fields.IncludeChildren, { propertyName: 'lbeIncludeChildrenProperty' }),
            groupGuidsProperty,
            groupTypesProperty,
            {
                modelName: 'severities',
                propertyType: PropertyType.MultiSelect,
                label: 'Severity',
                propertyName: 'lbeSeveritiesProperty',
                required: false,
                icon: 'fa-bell',
                extraData: {
                    options: severityLevels
                }
            },
            {
                modelName: 'categories',
                propertyType: PropertyType.Categories,
                label: 'Categories',
                propertyName: 'lbeCategoriesProperty',
                required: false,
                icon: 'fa-tags',
                extraData: {}
            }
        ];
        exportModeProperty.onChange = (viewValue: ExportMode) => {
            filters.forEach((f) => (f.hide = viewValue !== ExportMode.ByLogbookEntry));
            const isTriggerValid = this.isTriggerValid({
                connectionPointGuid: this.selectedItem.connectionPointGuid,
                exportMode: viewValue,
                trigger: this.selectedItem.trigger
            });
            this.formController[triggerField.propertyName].$setValidity('invalid', isTriggerValid);
            this.updateOptions(viewValue);
        };
        filters = filters.map((f) =>
            Object.assign(f, {
                extraData: Object.assign({}, f.extraData, {
                    subProperty: 'logbookEntriesFilter'
                })
            })
        );
        this._filters = filters;
        this.properties = properties.concat(filters);
    }

    isShowCustomTimestamp(connectionPointGuid: string, trigger: EventTrigger): boolean {
        const cp = this._connectionPoints[connectionPointGuid];
        const isApprovalOrRejection = trigger === EventTrigger.Approval || trigger === EventTrigger.Rejection;
        return cp.type === ConnectionPointType.HTTP && isApprovalOrRejection;
    }

    save(): angular.IPromise<void> {
        this._$rootscope.$broadcast(historianStorageConfigurationSaving);
        return this._configurationService
            .save([this.selectedItem])
            .then((data) => {
                this._$rootscope.$broadcast(historianStorageConfigurationSaved, data);
            })
            .catch((error) => {
                this._$rootscope.$broadcast(historianStorageConfigurationSaved);
            });
    }

    cancel() {
        this._$rootscope.$broadcast(historianStorageConfigurationSaved, null);
    }

    private updateVisibility(triggerType: number) {
        const isApproval: boolean = triggerType === EventTrigger.Approval || triggerType === EventTrigger.Rejection;
        const cp = this._connectionPoints[this.selectedItem?.connectionPointGuid];
        if (this._fieldsIndex) {
            // As discussed with HC, always allow them min and max, so don't hide them if isApproval
            const isShowCustomTimestamp = this.isShowCustomTimestamp(this.selectedItem?.connectionPointGuid, triggerType);
            this._fieldsIndex['groupGuids'].hide = !isApproval;
            this._fieldsIndex['groupTypes'].hide = !isApproval;
            this._fieldsIndex['isBoundToGroup'].hide = !isApproval;
            this._fieldsIndex['approvalLevel'].hide = !isApproval;
            this._fieldsIndex['exportQueryName'].hide = cp?.type !== ConnectionPointType.SQL;
            this._fieldsIndex['groupByPattern'].hide = cp?.type !== ConnectionPointType.SQL;
            this._fieldsIndex['isCustomTimestamp'].hide = !isShowCustomTimestamp;
            this.updateOptions(this.selectedItem?.exportMode);
            console.log(isShowCustomTimestamp);
        }
        if (this._filters) {
            this._filters.forEach((f) => (f.hide = this.selectedItem?.exportMode !== ExportMode.ByLogbookEntry));
        }
    }

    private updateOptions(mode: ExportMode) {
        const qsGuid = this._connectionPoints[this.selectedItem?.connectionPointGuid]?.querySetGuid;
        const namedQueries =
            mode === ExportMode.ByLogbookEntry ? this._namedQueries[qsGuid]?.logbookExportQueries ?? [] : this._namedQueries[qsGuid]?.exportQueries ?? [];
        this._fieldsIndex['exportQueryName'].extraData = { options: namedQueries.map((n) => ({ key: n, value: n })) };
        const readMode = this.properties.find((p) => p.modelName === Fields.ReadMode.modelName);
        if (readMode) {
            readMode.hide = mode !== ExportMode.ByLogbookEntry;
        }
    }

    private isTriggerValid(values: { connectionPointGuid: string; trigger: EventTrigger; exportMode: ExportMode }) {
        const { connectionPointGuid, trigger, exportMode } = values;
        const cp = this._connectionPoints[connectionPointGuid];
        const isTriggerValid = trigger !== EventTrigger.Manual || (exportMode === ExportMode.ByLogbookEntry && cp?.type === ConnectionPointType.SQL);
        return isTriggerValid;
    }

    private isExportModeValid(connectionPointGuid: string, exportMode: ExportMode) {
        const cp = this._connectionPoints[connectionPointGuid];
        const isExportModeValid = cp?.type === ConnectionPointType.SQL || exportMode === ExportMode.ByIdentifier;
        return isExportModeValid;
    }
}

export const wtModernHistorianStorageContextual: ComponentDefinition = {
    componentId: 'wtModernHistorianStorageContextual',
    controller: ModernHistorianStorageContextualController,
    controllerAs: 'vm',
    template: require('./modernHistorianStorageContextual.component.html'),
    bindings: {
        formController: '=',
        queries: '<',
        selectedItem: '<?'
    }
};
